'use client';

import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import { GetProjectNewsRequest, ProjectNews, ProjectNewsListResponse } from '@/app/api';
import useLazyLoad from '@/app/hooks/useLazyLoad';
import { useToast } from '@/app/hooks/useToast';
import { PropsWithClassname } from '@/app/types';
import { projectNewsApiForClient } from '@/app/utils/apiForClient';

import Button, { ButtonHTMLTag } from '@/app/components/Button/Button';
import Loader from '@/app/components/Loader/Loader';
import ProjectNewsPost from '@/app/components/ProjectNewsPost/ProjectNewsPost';
import SubprojectBanner from '@/app/components/SubprojectBanner/SubprojectBanner';
import { useIsUserInFloatingButtonGroup } from '@/app/hooks/useIsUserInFloatingButtonGroup';
import { getBreakpointFlags } from '@/app/utils/getBreakpointFlags/getBreakpointFlags';
import { useEffect } from 'react';

interface ProjectNewsTabProps extends PropsWithClassname {
  projectId: string;
  projectNumericId: number;
  initialData?: ProjectNews[];
  initialTotalItemsCount?: number;
  isSubprojectBannerVisible?: boolean;
}

const ProjectNewsTab = ({
  projectId,
  projectNumericId,
  initialData,
  initialTotalItemsCount,
  isSubprojectBannerVisible,
  className,
}: ProjectNewsTabProps) => {
  const { t } = useTranslation();
  const { showGenericErrorToast } = useToast();
  const isUserInFloatingButtonGroup = useIsUserInFloatingButtonGroup();

  useEffect(() => {
    const hashParam = window.location.hash;

    if (hashParam && hashParam.startsWith('#news-')) {
      const newsElement = document.getElementById(hashParam.replace('#news-', ''));

      const { isLgAndUp } = getBreakpointFlags();

      const offset = isLgAndUp || !isUserInFloatingButtonGroup ? 100 : 20; // Adjust offset for desktop and mobile

      if (newsElement) {
        const elementPosition = newsElement.getBoundingClientRect().top;
        const offsetPosition = window.pageYOffset + elementPosition - offset;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth',
        });
      }
    }
  }, []);

  const {
    isLoading,
    isLoadMoreAvailable,
    onLoadMore,
    listData: news,
  } = useLazyLoad<ProjectNewsListResponse, ProjectNews, GetProjectNewsRequest>({
    getItems: async (filter) => projectNewsApiForClient.getProjectNews(filter),
    listDataKey: 'projectNews',
    initialData,
    initialTotalCount: initialTotalItemsCount,
    filter: {
      project: projectId,
    },
    onError: showGenericErrorToast,
  });

  if (!news.length && !isLoading) {
    return <div className="my-[20px]">{t('components.ProjectNewsTab.noNewsYet')}</div>;
  }

  return (
    <div className={classNames(className, 'py-[8px]')}>
      <h3 className="mb-[20px] text-[20px] lg:text-[24px] leading-[24px] lg:leading-[32px] text-balance">
        {t('components.ProjectNewsTab.title')}
      </h3>
      <div>
        {news.map((post) => (
          <ProjectNewsPost key={post.id} newsPost={post} className="mb-[24px]" />
        ))}
      </div>
      {isLoading && <Loader />}
      {isLoadMoreAvailable && (
        <Button
          className={'self-center px-[24px] py-[20px]'}
          tagSpecificProps={{ htmlTag: ButtonHTMLTag.BUTTON, onClick: onLoadMore }}
        >
          {t('components.ProjectNewsTab.moreNews')}
        </Button>
      )}
      {isSubprojectBannerVisible && <SubprojectBanner projectNumericId={projectNumericId} className="mt-[40px]" />}
    </div>
  );
};

export default ProjectNewsTab;
